import React, { useState } from "react";
import { createPortal } from "react-dom";
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import WaterDropIcon from '@mui/icons-material/WaterDrop';

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

const ANIOS = Array.from({ length: 6 }, (_, i) => (2024 + i).toString());

const CondicionesSection = ({
  tipoBitacora = "Envasado",
  condicionesRecords = [],
  isFormOpen,
  setIsFormOpen,
  formData = {},
  setFormData,
  handleSaveCondiciones,
  handleDeleteCondiciones,
  onReloadCondiciones
}) => {
  const [selectedRecord, setSelectedRecord] = useState(null);

  const mesActual = MESES[new Date().getMonth()];
  const anioActual = new Date().getFullYear().toString();

  const selectedMes = formData?.mes || mesActual;
  const selectedAnio = formData?.anio || anioActual;
  const lecturas = formData?.lecturas || [
    { id: Date.now(), dia: 1, hora: "08:00", temp: "", hum: "" },
    { id: Date.now() + 1, dia: 1, hora: "14:00", temp: "", hum: "" }
  ];
  const observaciones = formData?.observaciones || "";

  const setField = (field, value) => {
    if (typeof setFormData === 'function') {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleAddLectura = (diaTarget = 1) => {
    const newLectura = {
      id: Date.now() + Math.random(),
      dia: Number(diaTarget),
      hora: "12:00",
      temp: "",
      hum: ""
    };
    if (typeof setFormData === 'function') {
      setFormData(prev => ({
        ...prev,
        lecturas: [...(prev.lecturas || lecturas), newLectura]
      }));
    }
  };

  const handleRemoveLectura = (id) => {
    if (typeof setFormData === 'function') {
      setFormData(prev => ({
        ...prev,
        lecturas: (prev.lecturas || lecturas).filter(item => item.id !== id)
      }));
    }
  };

  const handleLecturaChange = (id, field, value) => {
    if (typeof setFormData === 'function') {
      setFormData(prev => {
        const updated = (prev.lecturas || lecturas).map(item => {
          if (item.id === id) {
            return { ...item, [field]: field === 'dia' ? Number(value) : value };
          }
          return item;
        });
        return { ...prev, lecturas: updated };
      });
    }
  };

  const handleNewRecord = () => {
    if (typeof setFormData === 'function') {
      setFormData({
        id: null,
        dbId: null,
        id_bitacora_condiciones: null,
        mes: mesActual,
        anio: anioActual,
        lecturas: [
          { id: Date.now(), dia: 1, hora: "08:00", temp: "", hum: "" },
          { id: Date.now() + 1, dia: 1, hora: "14:00", temp: "", hum: "" },
          { id: Date.now() + 2, dia: 2, hora: "08:00", temp: "", hum: "" }
        ],
        observaciones: "",
        status: "borrador"
      });
    }
    setIsFormOpen(true);
  };

  const handleEditRecord = (rec) => {
    if (rec.status !== "borrador" && rec.status !== "rechazado" && rec.estatus !== "borrador" && rec.estatus !== "rechazado") {
      alert("Solo se pueden editar registros que se encuentren en estado BORRADOR o RECHAZADO.");
      return;
    }
    const realId = rec.id_bitacora_condiciones || rec.dbId || rec.id;
    if (typeof setFormData === 'function') {
      setFormData({
        id: realId,
        dbId: realId,
        id_bitacora_condiciones: realId,
        mes: rec.mes || rec.mesAnio?.split(" ")[0] || mesActual,
        anio: rec.anio || rec.mesAnio?.split(" ")[1] || anioActual,
        lecturas: rec.lecturas && rec.lecturas.length > 0 ? rec.lecturas : rec.dailyData || [],
        observaciones: rec.observaciones || "",
        status: rec.status || rec.estatus || "borrador"
      });
    }
    setIsFormOpen(true);
  };

  const handleSubmitForm = (statusToSet = "revision") => {
    const items = formData?.lecturas || lecturas;
    const validItems = items.filter(d => (d.temp !== "" && d.temp !== undefined && d.temp !== null) || (d.temperatura !== "" && d.temperatura !== undefined && d.temperatura !== null) || (d.hum !== "" && d.hum !== undefined && d.hum !== null) || (d.humedad !== "" && d.humedad !== undefined && d.humedad !== null));
    const avgTemp = validItems.length ? (validItems.reduce((acc, curr) => acc + parseFloat(curr.temp || curr.temperatura || 0), 0) / validItems.length).toFixed(1) : 0;
    const avgHum = validItems.length ? (validItems.reduce((acc, curr) => acc + parseFloat(curr.hum || curr.humedad || 0), 0) / validItems.length).toFixed(1) : 0;

    const recordToSave = {
      ...formData,
      id: formData.id || formData.dbId || formData.id_bitacora_condiciones,
      id_bitacora_condiciones: formData.id_bitacora_condiciones || formData.dbId || (typeof formData.id === "number" ? formData.id : undefined),
      mes: selectedMes,
      anio: selectedAnio,
      mesAnio: `${selectedMes} ${selectedAnio}`,
      lecturas: items,
      temperaturaPromedio: avgTemp,
      humedadPromedio: avgHum,
      id_inspector: formData.id_inspector || null,
      observaciones: formData.observaciones || observaciones || "",
      status: statusToSet,
      estatus: statusToSet,
      inspector: formData.inspector || (statusToSet === "revision" ? "Enviado a revisión" : "Borrador de usuario")
    };
    if (typeof handleSaveCondiciones === 'function') {
      handleSaveCondiciones(recordToSave);
    }
  };

  const validLecturas = lecturas.filter(l => l.temp !== "" || l.hum !== "");
  const avgTemp = validLecturas.length > 0
    ? (validLecturas.reduce((acc, curr) => acc + (parseFloat(curr.temp) || 0), 0) / validLecturas.length).toFixed(1)
    : "-";
  const avgHum = validLecturas.length > 0
    ? (validLecturas.reduce((acc, curr) => acc + (parseFloat(curr.hum) || 0), 0) / validLecturas.length).toFixed(1)
    : "-";

  // KPIs Generales
  const totalMonitoreos = condicionesRecords.length;
  const totalAprobados = condicionesRecords.filter(r => (r.status === 'aprobado' || r.estatus === 'aprobado')).length;
  const totalEnRevision = condicionesRecords.filter(r => (r.status === 'revision' || r.estatus === 'revision' || r.estatus === 'enviada')).length;

  return (
    <div className="animate-fadeIn">
      {/* HEADER DE LA SECCIÓN */}
      <div className="history-section-header" style={{ marginBottom: "20px" }}>
        <div>
          <h2 style={{ fontSize: "20px", fontWeight: 800, color: "var(--agave-green)" }}>
            Registro de Condiciones Ambientales Mensuales (Sección VIII)
          </h2>
          <p style={{ margin: "4px 0 0 0", color: "var(--text-muted)", fontSize: "13px" }}>
            Monitoreo continuo de Temperatura (ºC) y Humedad Relativa (%) en bodega y área de maduración
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {onReloadCondiciones && (
            <button
              type="button"
              onClick={onReloadCondiciones}
              className="btn-refresh-custom"
              title="Actualizar registros"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 14px",
                background: "rgba(255, 255, 255, 0.06)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                borderRadius: "10px",
                color: "#e2e8f0",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "13px"
              }}
            >
              <RefreshIcon style={{ fontSize: 18 }} />
              <span>Actualizar</span>
            </button>
          )}

          <button
            type="button"
            onClick={handleNewRecord}
            className="btn-add-new"
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px" }}
          >
            <AddIcon style={{ fontSize: 20 }} />
            <span style={{ fontWeight: 700 }}>Registrar Nuevo Mes</span>
          </button>
        </div>
      </div>

      {/* TABLA PRINCIPAL DE MESES */}
      <div className="records-list-wrapper" style={{ borderRadius: "14px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)" }}>
        <table className="records-table">
          <thead>
            <tr>
              <th>Mes / Año</th>
              <th>Lecturas Tomadas</th>
              <th>Temp. Promedio (ºC)</th>
              <th>Humedad Promedio (%)</th>
              <th>Observaciones</th>
              <th>Revisor (V.Bo. Inspector)</th>
              <th>Estado</th>
              <th style={{ textAlign: "center" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {condicionesRecords.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
                  <DeviceThermostatIcon style={{ fontSize: 40, opacity: 0.4, marginBottom: "8px" }} />
                  <p style={{ margin: 0, fontSize: "14px" }}>No hay registros de condiciones ambientales para ningún mes.</p>
                </td>
              </tr>
            ) : (
              condicionesRecords.map((rec, i) => {
                const items = rec.lecturas || rec.registros || rec.dailyData || [];
                const validItems = items.filter(d => (d.temp !== "" && d.temp !== null && d.temp !== undefined) || (d.temperatura !== "" && d.temperatura !== null && d.temperatura !== undefined) || (d.hum !== "" && d.hum !== null && d.hum !== undefined) || (d.humedad !== "" && d.humedad !== null && d.humedad !== undefined));
                const avgTempHist = validItems.length ? (validItems.reduce((acc, curr) => acc + parseFloat(curr.temp || curr.temperatura || 0), 0) / validItems.length).toFixed(1) : (rec.temperaturaPromedio !== undefined && rec.temperaturaPromedio !== null ? rec.temperaturaPromedio : (rec.temperatura_promedio ?? "-"));
                const avgHumHist = validItems.length ? (validItems.reduce((acc, curr) => acc + parseFloat(curr.hum || curr.humedad || 0), 0) / validItems.length).toFixed(1) : (rec.humedadPromedio !== undefined && rec.humedadPromedio !== null ? rec.humedadPromedio : (rec.humedad_promedio ?? "-"));

                const currentStatus = rec.status || rec.estatus || "borrador";
                const isEditable = currentStatus === "borrador" || currentStatus === "rechazado";

                return (
                  <tr key={rec.id || rec.id_bitacora_condiciones || i}>
                    <td style={{ fontWeight: 800, color: "var(--agave-green)", fontSize: "15px" }}>
                      {rec.mes || rec.mesAnio} {rec.anio || ''}
                    </td>
                    <td>
                      <strong>{items.length}</strong> lecturas registradas
                    </td>
                    <td style={{ color: "var(--copper)", fontWeight: 700 }}>
                      {avgTempHist} {avgTempHist !== "-" ? "°C" : ""}
                    </td>
                    <td style={{ color: "#3498db", fontWeight: 700 }}>
                      {avgHumHist} {avgHumHist !== "-" ? "%" : ""}
                    </td>
                    <td style={{ maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={rec.observaciones}>
                      {rec.observaciones || "Sin observaciones"}
                    </td>
                    <td style={{ fontSize: "12px", fontWeight: 700, color: "#1e293b" }}>
                      {rec.inspector || rec.vbo_inspector || (currentStatus === "borrador" ? "Borrador de usuario" : "Pendiente de V.Bo.")}
                    </td>
                    <td>
                      <span className={`badge-status ${currentStatus}`}>
                        {currentStatus === "aprobado" ? "Aprobado" : (currentStatus === "revision" || currentStatus === "enviada") ? "En revisión" : (currentStatus === "rechazado" ? "Rechazado" : "Borrador")}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div style={{ display: "flex", justifyContent: "center", gap: "6px" }}>
                        <button
                          type="button"
                          onClick={() => setSelectedRecord(rec)}
                          title="Ver Detalle Mensual"
                          style={{ background: "rgba(52, 152, 219, 0.15)", color: "#3498db", border: "1px solid rgba(52, 152, 219, 0.3)", borderRadius: "6px", padding: "5px 8px", cursor: "pointer", display: "flex", alignItems: "center" }}
                        >
                          <VisibilityIcon style={{ fontSize: 16 }} />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleEditRecord(rec)}
                          disabled={!isEditable}
                          title={isEditable ? "Editar Registro" : "Solo editables en estado Borrador o Rechazado"}
                          style={{
                            background: isEditable ? "rgba(241, 196, 15, 0.15)" : "rgba(255,255,255,0.04)",
                            color: isEditable ? "#f1c40f" : "var(--text-muted)",
                            border: isEditable ? "1px solid rgba(241, 196, 15, 0.3)" : "1px solid rgba(255,255,255,0.08)",
                            borderRadius: "6px",
                            padding: "5px 8px",
                            cursor: isEditable ? "pointer" : "not-allowed",
                            display: "flex",
                            alignItems: "center"
                          }}
                        >
                          <EditIcon style={{ fontSize: 16 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* =========================================================================
          MODAL 1: FORMULARIO NUEVO REGISTRO / EDICIÓN SECCIÓN VIII
      ========================================================================= */}
      {isFormOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsFormOpen(false)}>
          <div
            className="modal-dialog-custom modal-xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "980px" }}
          >
            {/* Header del Modal */}
            <div className="modal-header-custom header-theme-condiciones">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <DeviceThermostatIcon />
                </div>
                <div>
                  <h3>
                    {formData.id && condicionesRecords.some(r => r.id === formData.id || r.id_bitacora_condiciones === formData.id)
                      ? `Editar Monitoreo Ambiental: ${selectedMes} ${selectedAnio}`
                      : `Captura de Condiciones Ambientales Mensuales (Sección VIII - ${tipoBitacora})`}
                  </h3>
                  <p>Temperatura (°C) y Humedad Relativa (%) en bodega y área de {tipoBitacora.toLowerCase()}</p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsFormOpen(false)}
                title="Cerrar ventana"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Formulario en Modal */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmitForm("revision");
              }}
              className="modal-body-scrollable"
              style={{ display: "flex", flexDirection: "column", gap: "16px", background: "#F8FAFC" }}
            >
              {/* PASO 1: SELECCION DE MES Y AÑO */}
              <section style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                <header style={{ marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "34px", height: "34px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(13, 148, 136, 0.12)", color: "#0D9488" }}>
                    <CalendarMonthIcon style={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: "14px", fontWeight: 800, margin: 0, color: "#0F172A", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      Paso 1: Período del Monitoreo
                    </h4>
                    <p style={{ margin: "2px 0 0 0", color: "#64748B", fontSize: "12px" }}>
                      Seleccione el mes y año de registro de bitácora
                    </p>
                  </div>
                </header>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "10px" }}>
                  <div className="integrated-input-box">
                    <label className="integrated-input-label">MES DE MONITOREO *</label>
                    <select
                      value={selectedMes}
                      onChange={e => setField("mes", e.target.value)}
                      className="integrated-select-control"
                      style={{ fontWeight: 700 }}
                    >
                      {MESES.map((m, idx) => (
                        <option key={idx} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">AÑO *</label>
                    <select
                      value={selectedAnio}
                      onChange={e => setField("anio", e.target.value)}
                      className="integrated-select-control"
                      style={{ fontWeight: 700 }}
                    >
                      {ANIOS.map((a, idx) => (
                        <option key={idx} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* RESUMEN MÉTRICO EN TIEMPO REAL */}
                <div style={{ marginTop: "14px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "12px 16px" }}>
                  <div>
                    <span style={{ fontSize: "10.5px", color: "#64748B", textTransform: "uppercase", fontWeight: 800, display: "block" }}>Tomas Registradas</span>
                    <strong style={{ fontSize: "17px", color: "#0F172A" }}>{validLecturas.length}</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: "10.5px", color: "#64748B", textTransform: "uppercase", fontWeight: 800, display: "block" }}>Temp. Promedio Estimada</span>
                    <strong style={{ fontSize: "17px", color: "var(--copper)" }}>{avgTemp} {avgTemp !== "-" ? "°C" : ""}</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: "10.5px", color: "#64748B", textTransform: "uppercase", fontWeight: 800, display: "block" }}>Humedad Promedio Estimada</span>
                    <strong style={{ fontSize: "17px", color: "#0284C7" }}>{avgHum} {avgHum !== "-" ? "%" : ""}</strong>
                  </div>
                </div>
              </section>

              {/* PASO 2: TOMAS DE LECTURA */}
              <section style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{ width: "34px", height: "34px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(210, 105, 30, 0.12)", color: "var(--copper)" }}>
                      <DeviceThermostatIcon style={{ fontSize: 20 }} />
                    </div>
                    <div>
                      <h4 style={{ fontSize: "14px", fontWeight: 800, margin: 0, color: "#0F172A", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        Paso 2: Registro Diario de Lecturas
                      </h4>
                      <p style={{ margin: "2px 0 0 0", color: "#64748B", fontSize: "12px" }}>
                        Capture las mediciones de los días correspondientes
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleAddLectura(lecturas.length > 0 ? (lecturas[lecturas.length - 1].dia || 1) : 1)}
                    className="btn-add-new"
                    style={{ background: "rgba(46, 204, 113, 0.15)", color: "#2ecc71", border: "1px solid rgba(46, 204, 113, 0.4)", padding: "7px 14px", borderRadius: "8px", fontSize: "12.5px" }}
                  >
                    <AddIcon style={{ fontSize: 17 }} />
                    <span style={{ fontWeight: 700 }}>+ Añadir Lectura</span>
                  </button>
                </header>

                <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "300px", overflowY: "auto", paddingRight: "4px" }}>
                  {lecturas.map((item, index) => (
                    <div
                      key={item.id || index}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "100px 140px 1fr 1fr 40px",
                        gap: "8px",
                        alignItems: "center",
                        background: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                        borderRadius: "10px",
                        padding: "6px 10px"
                      }}
                    >
                      <div className="integrated-input-box" style={{ minHeight: "46px", padding: "4px 8px" }}>
                        <label className="integrated-input-label" style={{ fontSize: "9px" }}>DÍA</label>
                        <input
                          type="number"
                          min="1"
                          max="31"
                          value={item.dia || 1}
                          onChange={e => handleLecturaChange(item.id, "dia", e.target.value)}
                          className="integrated-input-control"
                          style={{ fontWeight: 800, fontSize: "13px" }}
                        />
                      </div>

                      <div className="integrated-input-box" style={{ minHeight: "46px", padding: "4px 8px" }}>
                        <label className="integrated-input-label" style={{ fontSize: "9px" }}>HORA</label>
                        <input
                          type="time"
                          value={item.hora || "08:00"}
                          onChange={e => handleLecturaChange(item.id, "hora", e.target.value)}
                          className="integrated-input-control"
                          style={{ fontSize: "12.5px" }}
                        />
                      </div>

                      <div className="integrated-input-box" style={{ minHeight: "46px", padding: "4px 8px" }}>
                        <label className="integrated-input-label" style={{ fontSize: "9px" }}>TEMPERATURA</label>
                        <input
                          type="number"
                          step="0.1"
                          placeholder="22.5"
                          value={item.temp}
                          onChange={e => handleLecturaChange(item.id, "temp", e.target.value)}
                          className="integrated-input-control"
                          style={{ paddingRight: "30px", fontWeight: 700 }}
                        />
                        <span className="integrated-input-suffix" style={{ fontSize: "10.5px", padding: "2px 6px" }}>°C</span>
                      </div>

                      <div className="integrated-input-box" style={{ minHeight: "46px", padding: "4px 8px" }}>
                        <label className="integrated-input-label" style={{ fontSize: "9px" }}>% HUMEDAD</label>
                        <input
                          type="number"
                          step="0.1"
                          placeholder="55"
                          value={item.hum}
                          onChange={e => handleLecturaChange(item.id, "hum", e.target.value)}
                          className="integrated-input-control"
                          style={{ paddingRight: "28px", fontWeight: 700 }}
                        />
                        <span className="integrated-input-suffix" style={{ fontSize: "10.5px", padding: "2px 6px" }}>%</span>
                      </div>

                      <div style={{ textAlign: "center" }}>
                        {lecturas.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveLectura(item.id)}
                            title="Eliminar lectura"
                            style={{ background: "rgba(231, 76, 60, 0.12)", color: "#e74c3c", border: "none", borderRadius: "6px", padding: "6px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <DeleteIcon style={{ fontSize: 16 }} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* PASO 3: OBSERVACIONES */}
              <section style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                <header style={{ marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "34px", height: "34px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(13, 148, 136, 0.12)", color: "#0D9488" }}>
                    <SettingsIcon style={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: "14px", fontWeight: 800, margin: 0, color: "#0F172A", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      Paso 3: Observaciones y Notas Generales
                    </h4>
                    <p style={{ margin: "2px 0 0 0", color: "#64748B", fontSize: "12px" }}>
                      Anomalías climáticas, calibración de higrómetros o notas del responsable
                    </p>
                  </div>
                </header>

                <div className="integrated-input-box" style={{ minHeight: "75px", justifyContent: "flex-start", padding: "8px 14px" }}>
                  <label className="integrated-input-label">OBSERVACIONES GENERALES DEL MES</label>
                  <textarea
                    value={formData?.observaciones || observaciones}
                    onChange={e => setField("observaciones", e.target.value)}
                    className="integrated-input-control"
                    style={{ height: "42px", resize: "none" }}
                    placeholder="Escriba aquí observaciones climáticas del mes..."
                  />
                </div>
              </section>

              {/* Botones del Modal */}
              <div className="modal-footer-custom" style={{ marginTop: "6px", borderRadius: "12px", background: "#FFFFFF", padding: "14px 20px" }}>
                <button
                  type="button"
                  className="btn-cancel-modal"
                  onClick={() => setIsFormOpen(false)}
                >
                  Cancelar
                </button>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    className="btn-secondary-custom"
                    style={{ padding: "9px 18px", fontSize: "12.5px" }}
                    onClick={() => handleSubmitForm("borrador")}
                  >
                    Guardar Borrador
                  </button>
                  <button
                    type="submit"
                    className="btn-submit-modal"
                    style={{ background: "linear-gradient(135deg, #0D9488 0%, #0F766E 100%)", boxShadow: "0 4px 14px rgba(13, 148, 136, 0.35)" }}
                  >
                    <CheckCircleIcon style={{ fontSize: 18 }} />
                    <span>{formData.id && condicionesRecords.some(r => r.id === formData.id || r.id_bitacora_condiciones === formData.id) ? "Guardar Cambios" : "Registrar Monitoreo Sección VIII"}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          MODAL 2: DETALLE COMPLETO DEL MES (MONITOREO AMBIENTAL)
      ========================================================================= */}
      {selectedRecord && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setSelectedRecord(null)}>
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "800px" }}
          >
            {/* Header del Modal */}
            <div className="detail-modal-header header-theme-condiciones">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <DeviceThermostatIcon />
                </div>
                <div>
                  <h3 style={{ margin: 0, color: "white" }}>
                    Monitoreo: {selectedRecord.mes || selectedRecord.mesAnio} {selectedRecord.anio || ''}
                  </h3>
                  <p style={{ margin: "2px 0 0 0", color: "rgba(255,255,255,0.85)", fontSize: "12px" }}>
                    Detalle de Condiciones Ambientales de las Instalaciones de {tipoBitacora} (Sección VIII)
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setSelectedRecord(null)}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Contenido del Detalle */}
            <div className="detail-modal-body" style={{ padding: "24px" }}>
              {(() => {
                const recLecturas = selectedRecord.lecturas || selectedRecord.registros || selectedRecord.dailyData || [];
                const validRecs = recLecturas.filter(l => (l.temp !== "" && l.temp !== null && l.temp !== undefined) || (l.temperatura !== "" && l.temperatura !== null && l.temperatura !== undefined) || (l.hum !== "" && l.hum !== null && l.hum !== undefined) || (l.humedad !== "" && l.humedad !== null && l.humedad !== undefined));
                const recAvgTemp = validRecs.length > 0 ? (validRecs.reduce((a, b) => a + (parseFloat(b.temp || b.temperatura || 0)), 0) / validRecs.length).toFixed(1) : (selectedRecord.temperaturaPromedio !== undefined && selectedRecord.temperaturaPromedio !== null ? selectedRecord.temperaturaPromedio : (selectedRecord.temperatura_promedio ?? "-"));
                const recAvgHum = validRecs.length > 0 ? (validRecs.reduce((a, b) => a + (parseFloat(b.hum || b.humedad || 0)), 0) / validRecs.length).toFixed(1) : (selectedRecord.humedadPromedio !== undefined && selectedRecord.humedadPromedio !== null ? selectedRecord.humedadPromedio : (selectedRecord.humedad_promedio ?? "-"));
                const detStatus = selectedRecord.status || selectedRecord.estatus || "borrador";

                return (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "12px", padding: "16px", marginBottom: "20px" }}>
                      <div>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 700, display: "block" }}>Mes Monitoreado</span>
                        <strong style={{ fontSize: "16px", color: "var(--agave-green)" }}>{selectedRecord.mes || selectedRecord.mesAnio} {selectedRecord.anio || ''}</strong>
                      </div>
                      <div>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 700, display: "block" }}>Total Lecturas</span>
                        <strong style={{ fontSize: "16px" }}>{recLecturas.length}</strong>
                      </div>
                      <div>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 700, display: "block" }}>Temp. Promedio</span>
                        <strong style={{ fontSize: "16px", color: "var(--copper)" }}>{recAvgTemp} {recAvgTemp !== "-" ? "°C" : ""}</strong>
                      </div>
                      <div>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 700, display: "block" }}>Humedad Promedio</span>
                        <strong style={{ fontSize: "16px", color: "#3498db" }}>{recAvgHum} {recAvgHum !== "-" ? "%" : ""}</strong>
                      </div>
                      <div>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 700, display: "block" }}>Estado</span>
                        <span className={`badge-status ${detStatus}`}>
                          {detStatus === "aprobado" ? "Aprobado" : (detStatus === "revision" || detStatus === "enviada") ? "En revisión" : (detStatus === "rechazado" ? "Rechazado" : "Borrador")}
                        </span>
                      </div>
                    </div>

                    <h4 style={{ margin: "0 0 12px 0", fontSize: "14px", fontWeight: 800 }}>Tomas de Lectura del Período</h4>
                    <div style={{ maxHeight: "240px", overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: "10px", marginBottom: "16px" }}>
                      <table className="records-table" style={{ margin: 0 }}>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Día</th>
                            <th>Hora</th>
                            <th>Temperatura (°C)</th>
                            <th>Humedad (%)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recLecturas.length === 0 ? (
                            <tr>
                              <td colSpan="5" style={{ textAlign: "center", padding: "20px", color: "var(--text-muted)" }}>
                                Sin lecturas individuales detalladas.
                              </td>
                            </tr>
                          ) : (
                            recLecturas.map((l, idx) => (
                              <tr key={idx}>
                                <td>{idx + 1}</td>
                                <td>Día {l.dia}</td>
                                <td>{l.hora}</td>
                                <td style={{ color: "var(--copper)", fontWeight: 700 }}>
                                  {(l.temp !== "" && l.temp !== null && l.temp !== undefined) ? `${l.temp} °C` : ((l.temperatura !== "" && l.temperatura !== null && l.temperatura !== undefined) ? `${l.temperatura} °C` : "-")}
                                </td>
                                <td style={{ color: "#3498db", fontWeight: 700 }}>
                                  {(l.hum !== "" && l.hum !== null && l.hum !== undefined) ? `${l.hum} %` : ((l.humedad !== "" && l.humedad !== null && l.humedad !== undefined) ? `${l.humedad} %` : "-")}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {selectedRecord.observaciones && (
                      <div style={{ padding: "12px", background: "#F8FAFC", borderRadius: "8px", border: "1px solid #E2E8F0", fontSize: "13px", marginBottom: "12px" }}>
                        <strong>Observaciones Asociado:</strong>
                        <p style={{ margin: "4px 0 0 0", color: "#64748B" }}>{selectedRecord.observaciones}</p>
                      </div>
                    )}

                    {selectedRecord.observacionesCorreccion && (
                      <div style={{ padding: "12px", background: "rgba(231, 76, 60, 0.08)", borderRadius: "8px", border: "1px solid rgba(231, 76, 60, 0.25)", fontSize: "13px" }}>
                        <strong style={{ color: "#c0392b" }}>Observaciones de la Unidad de Inspección:</strong>
                        <p style={{ margin: "4px 0 0 0", color: "#c0392b" }}>{selectedRecord.observacionesCorreccion}</p>
                      </div>
                    )}
                  </>
                );
              })()}

              <div className="modal-footer-custom" style={{ marginTop: "20px", borderRadius: "10px" }}>
                <button
                  type="button"
                  onClick={() => setSelectedRecord(null)}
                  className="btn-cancel-modal"
                >
                  Cerrar
                </button>
                {(selectedRecord.status === "borrador" || selectedRecord.status === "rechazado" || selectedRecord.estatus === "borrador" || selectedRecord.estatus === "rechazado") && (
                  <button
                    type="button"
                    onClick={() => {
                      const rec = selectedRecord;
                      setSelectedRecord(null);
                      handleEditRecord(rec);
                    }}
                    className="btn-submit-modal"
                    style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", boxShadow: "0 4px 14px rgba(245, 158, 11, 0.3)" }}
                  >
                    <EditIcon style={{ fontSize: 16 }} />
                    <span>Editar Registro</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CondicionesSection;
